import React, { createContext, useCallback, useContext, useEffect, useSyncExternalStore } from 'react';

/* ── PAGER-2 — EL CANAL DE LA PANE ────────────────────────────────────────────
 *
 * Hermano de `activacion.js`, y con la misma disciplina: una sola pregunta por
 * módulo. Aquél contesta "¿el usuario me está viendo?"; éste contesta las DOS
 * cosas que una raíz necesita saber cuando deja de estar sola en la pantalla y
 * pasa a vivir dentro de un carril:
 *
 *   1. ¿DÓNDE SCROLLEO? Hasta PAGER-1 la respuesta era única y global: el
 *      `<main data-testid="content-area">` del chasis, un solo nodo para las
 *      cuatro raíces. Con el carril cada pane es su PROPIO scroller (así el
 *      scroll se conserva por pestaña y deja de heredarse entre pantallas
 *      distintas — el defecto P2 del censo), así que "el scrollport" ya no es
 *      una constante: es de quien pregunta.
 *
 *   2. ¿HAY UNA CAPA ENCIMA DE MÍ? El swipe entre pestañas pasó de vivir en las
 *      cuatro raíces (×4 juegos de listeners) a vivir UNA vez en el carril. El
 *      carril, sin embargo, no puede saber si la raíz de enfrente tiene un modal
 *      abierto: ese censo vive —y se queda— dentro de cada pantalla. Cada raíz
 *      lo REPORTA por aquí y el carril lee el de la que está enfrente.
 *
 * EL DEFAULT SIN PROVEEDOR ES "NO HAY PAGER", y sostiene lo mismo que el `true`
 * de `activacion.js`: la web, las sub-navegaciones, Ajustes, Pendientes y TODOS
 * los arneses viejos siguen exactamente igual. Sin proveedor:
 *   · `useScrollportRaiz()` devuelve el `<main>` del chasis, que es la verdad de
 *     hoy en web y en cualquier montaje suelto;
 *   · `useReportarCapa()` no hace absolutamente nada — ni un efecto con cuerpo,
 *     ni una escritura.
 *
 * POR QUÉ EL REPORTE ES UNA FUNCIÓN Y NO ESTADO DE REACT. El carril guarda lo
 * reportado en un ref y `useTabSwipe` lo lee al `touchstart` (su `enabled`
 * acepta una función justamente para esto). Si fuera estado, abrir un modal en
 * el Panel re-renderizaría el carril entero —las cuatro raíces vivas— para
 * cambiar un booleano que sólo se consulta cuando un dedo toca la pantalla.
 * Aquí no se re-renderiza nadie.
 *
 * ── TANDA 19 (#210) — Y LA TERCERA PREGUNTA: ¿SOY LA QUE SE ESTÁ VIENDO? ────
 *
 * `activacion.js` contesta "¿el ROUTER dice que soy yo?", y esa respuesta llega
 * TARDE: el gesto asienta el carril en la vecina y el router no se entera hasta
 * `TAB_SLIDE_MS` después, así que durante todo ese tramo la pantalla que el
 * usuario tiene delante y la que el router cree activa son distintas. Para casi
 * todo eso da igual —red, analíticas, relojes: TODOS deben esperar al router, y
 * esa doctrina no se toca—. Da igual salvo para UNA cosa: lo que se PINTA
 * anclado al viewport, que no pertenece a ninguna caja del carril y por tanto no
 * puede pertenecer a una pane dormida (el letrero de `StudyCenter` lo argumenta
 * entero). Ahí la pregunta correcta no es "¿aterrizó el router?" sino "¿soy la
 * que se ve AHORA?".
 *
 * QUIÉN ESCRIBE Y QUIÉN LEE. Escribe el carril, una sola vez por transición,
 * con la ruta que el gesto YA calcula para sus vecinas y sus capas; leen las
 * raíces que pintan algo anclado al viewport. Es un canal EXTERNO con
 * suscripción —no estado de React— por lo mismo que el reporte de capa vive en
 * un ref: publicar no puede re-rendear las cuatro raíces. Lo que sí se rendea es
 * la raíz cuyo veredicto CAMBIÓ, y sólo ella: cada suscriptor compara su propio
 * booleano, así que una transición despierta como mucho a la que gana la barra y
 * a la que la pierde.
 *
 * SIN PUBLICACIÓN, `null`. Web, montajes sueltos, arneses viejos y taps de la
 * píldora que nunca pasan por el gesto: el lector recibe `null` y cae a `activa`,
 * que es el comportamiento de hoy carácter por carácter. El canal no manda
 * cuando no tiene nada que decir.
 */

/** `{ scrollportRef, reportarCapa }` de ESTA pane, o `null` si no hay pager. */
export const PanePagerContext = createContext(null);


/** Lo pone el carril por encima de cada una de las cuatro raíces. `valor` tiene
 *  que ser estable entre renders: el carril lo construye una sola vez por ruta. */
export function PanePagerProvider({ valor, children }) {
  return (
    <PanePagerContext.Provider value={valor || null}>
      {children}
    </PanePagerContext.Provider>
  );
}

/**
 * El nodo que scrollea para esta raíz. Devuelve una FUNCIÓN, no el nodo: quien
 * la llama lo hace en el instante de escribir (un efecto de layout, un handler),
 * cuando el ref ya está lleno y el DOM ya es el bueno.
 *
 * Sin pager —web, montaje suelto, arnés— cae al `<main>` del chasis, que es
 * exactamente lo que estas pantallas hacían antes de que existiera el carril.
 */
export function useScrollportRaiz() {
  const pane = useContext(PanePagerContext);
  return useCallback(() => {
    const propio = pane && pane.scrollportRef && pane.scrollportRef.current;
    if (propio) return propio;
    try {
      return document.querySelector('[data-testid="content-area"]');
    } catch (e) {
      return null;
    }
  }, [pane]);
}

/**
 * Le dice al carril si ESTA raíz tiene una capa encima (modal, cortina, detalle)
 * para que el swipe entre pestañas no nazca por debajo de ella. El censo de qué
 * cuenta como capa se queda donde siempre estuvo: en cada pantalla.
 *
 * Al desmontarse la raíz se reporta `false`: una capa no puede sobrevivir a la
 * pantalla que la abrió.
 */
export function useReportarCapa(capaEncima) {
  const pane = useContext(PanePagerContext);
  const reportar = pane && pane.reportarCapa;
  useEffect(() => {
    if (typeof reportar !== 'function') return undefined;
    reportar(Boolean(capaEncima));
    return () => reportar(false);
  }, [reportar, capaEncima]);
}

/**
 * EL CANAL DE LA RUTA VISIBLE (TANDA 19 / #210). Uno solo para las cuatro panes
 * —lo que se publica es una ruta, no un booleano por pane—, construido por el
 * carril una vez y compartido por referencia dentro del canal de cada pane.
 *
 * `publicar(null)` significa "no tengo nada que decir, manda el router", y es el
 * estado de reposo: se vuelve a él en cuanto React confirma la navegación. Los
 * suscriptores sólo se avisan cuando el valor CAMBIA de verdad, así que
 * republicar lo mismo no cuesta un render.
 */
export function crearCanalRutaVista() {
  let ruta = null;
  const suscriptores = new Set();
  return {
    leer: () => ruta,
    publicar(valor) {
      const siguiente = valor || null;
      if (siguiente === ruta) return;
      ruta = siguiente;
      suscriptores.forEach((avisar) => {
        try { avisar(); } catch (e) { /* un lector roto no calla a los demás */ }
      });
    },
    suscribir(avisar) {
      suscriptores.add(avisar);
      return () => { suscriptores.delete(avisar); };
    },
  };
}

/**
 * ¿ESTA RAÍZ ES LA QUE EL USUARIO TIENE DELANTE AHORA MISMO?
 *
 *   `true`  — el carril dice que sí.
 *   `false` — el carril dice que es otra.
 *   `null`  — NADIE lo dice (sin carril, o el carril en reposo): quien pregunta
 *             tiene que caer a `useRaizActiva()`, que es la conducta de hoy.
 *
 * Se lee con `useSyncExternalStore` y no con estado + efecto a propósito: el
 * carril publica DENTRO del gesto, y una suscripción montada en un efecto se
 * perdería la publicación que ocurre entre el render y el efecto. Aquí no hay
 * ventana: el veredicto que se pinta es siempre el último publicado.
 */
export function useEsRutaVista() {
  const pane = useContext(PanePagerContext);
  const canal = (pane && pane.rutaVista) || null;
  const miRuta = (pane && pane.ruta) || null;

  const suscribir = useCallback((avisar) => {
    if (!canal) return () => {};
    return canal.suscribir(avisar);
  }, [canal]);

  // Devuelve SIEMPRE `null`/`true`/`false` —primitivos— así que la comparación
  // de identidad que hace React no puede producir un bucle de renders.
  const leer = useCallback(() => {
    if (!canal || !miRuta) return null;
    const vista = canal.leer();
    return vista == null ? null : vista === miRuta;
  }, [canal, miRuta]);

  return useSyncExternalStore(suscribir, leer, leer);
}

/**
 * ¿DÓNDE ESTÁ LA CAJA DE MI PÁGINA? (TANDA 21 / #212)
 *
 * Devuelve el nodo del MARCO de esta pane —la caja que ocupa su hueco en el
 * riel, que no scrollea y que es `relative`— o `null` si no hay carril.
 *
 * PARA QUÉ SIRVE. Una raíz puede tener MUEBLES: cosas que no van en el flujo del
 * contenido ni se mueven con su scroll, pero que SÍ son de la página y tienen
 * que entrar y salir deslizándose con ella. Hoy hay uno: el recuadro del botón
 * de estudiar. Anclarlo a la pantalla —que es lo que hacía— lo dejaba fuera de
 * la hoja: mientras la página entraba, el recuadro se quedaba quieto en su sitio
 * y el ojo lo leía como un componente aparte que aparece al final.
 *
 * QUIÉN LO USA Y CÓMO. La raíz monta su mueble aquí por portal. Tiene que ser un
 * portal porque quien lo pinta vive DENTRO del scroller y el mueble tiene que
 * vivir FUERA —si no, se iría con el scroll—; el destino es su propio marco, no
 * una capa común, y esa es toda la diferencia con lo que había antes.
 *
 * SIN CARRIL DEVUELVE `null` y quien pregunta se pinta donde siempre. Es el
 * mismo respaldo que el `true` de `useRaizActiva` y el `null` de
 * `useEsRutaVista`: la web, las sub-navegaciones y todos los arneses viejos
 * conservan el árbol que ya tenían, carácter por carácter.
 */
export function useMarcoDeLaPane() {
  const pane = useContext(PanePagerContext);
  const ref = pane && pane.marco;
  return (ref && ref.current) || null;
}

export default useReportarCapa;
